import { Box, Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { MprcContainer, MprcFormSubHeader, MprcStringToHtml } from "components";
import { MprcSwitch } from "components/MprcSwitch";
import { AutoCompleteDataModelDto, QuestionType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ALTERNATIVE_FALSE } from "views/assessment/common/assessment";
import { useQuestionContext } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { PublicationSelectorDrawer } from "views/knowledge/publication/drawers/PublicationSelectorDrawer";
import { TrainingSelectorDrawer } from "views/lms/training/drawers/TrainingSelectorDrawer";

export interface IPostValidationFeedback {
    trainingRecommended: string[] | undefined;
    knowledgePublicationRecommended: string[] | undefined;
}

export const EditQuestionFeedback: React.FC = () => {
    const { viewData, readonly, postValidation, handleUpdateRecord, handleUpdateEvaluation } = useQuestionContext();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const currentPostValidation = postValidation.feedbackBox;
    const alternativesWrong = viewData.alternatives?.filter(x => x.text.length > 0 && (x.evaluation?.correctAnswer == ALTERNATIVE_FALSE || (viewData.questionType != QuestionType.SimpleChoice && viewData.questionType != QuestionType.MultipleChoice)));

    const [alternativeId, setAlternativeId] = useState<string>(alternativesWrong.length > 0 ? alternativesWrong[0].alternativeId : viewData.alternatives[0].alternativeId);
    const [dialog, setDialog] = useState<React.ReactNode>();

    const publicationRecommended = viewData.differentFeedback ? viewData.alternatives.find(x => x.alternativeId == alternativeId)?.publicationRecommended ?? [] : viewData.publicationRecommended;
    const trainingRecommended = viewData.differentFeedback ? viewData.alternatives.find(x => x.alternativeId == alternativeId)?.trainingRecommended ?? [] : viewData.trainingRecommended;
    const totalRecomendation = publicationRecommended.length + trainingRecommended.length;

    ///
    function handleUpdateAlternativeFeedback(value: string) {
        var newValue = viewData.alternatives.map(x => {
            if (x.alternativeId == alternativeId && x.evaluation)
                x.evaluation.feedback = value;
            return x;
        });
        handleUpdateRecord("alternatives", newValue);
    }

    ///
    function handleUpdatePublication(value: AutoCompleteDataModelDto[]) {
        if (viewData.differentFeedback) {
            var newValue = viewData.alternatives.map(x => {
                if (x.alternativeId == alternativeId && x.evaluation)
                    x.publicationRecommended = value;
                return x;
            });
            handleUpdateRecord("alternatives", newValue);
        }  else {
            handleUpdateRecord("publicationRecommended", value);
        }
    }

    ///
    function handleUpdateTraining(value: AutoCompleteDataModelDto[]) {
        if (viewData.differentFeedback) {
            var newValue = viewData.alternatives.map(x => {
                if (x.alternativeId == alternativeId && x.evaluation)
                    x.trainingRecommended = value;
                return x;
            });
            handleUpdateRecord("alternatives", newValue);
        } else {
            handleUpdateRecord("trainingRecommended", value);
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (!alternativesWrong?.some(x => x.alternativeId == alternativeId)) {
            setAlternativeId(alternativesWrong.length > 0 ? alternativesWrong[0].alternativeId : viewData.alternatives[0].alternativeId);
            if (alternativesWrong.length == 0) {
                handleUpdateRecord("differentFeedback", false);
            }
        }
    }, [viewData.alternatives])

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            { dialog }

            <MprcContainer>
                <MprcSwitch
                    label={t("Question.Edit.Feedback.DefineDifFeedbackLabel")}
                    checked={viewData.differentFeedback}
                    disabled={readonly || alternativesWrong.length == 0}
                    onChange={(e) => handleUpdateRecord("differentFeedback", e.target.checked)}
                />
            </MprcContainer>
            {
                viewData.differentFeedback
                    ?
                    <MprcContainer>
                        <Box paddingY={1} sx={
                            (theme) => ({
                                backgroundColor: theme.palette.notice.main,
                                borderRadius: "6px"
                            })
                        }>
                            <FormControl fullWidth>
                                <InputLabel>{t("Question.Edit.Feedback.SelectAlternativeLabel")}</InputLabel>
                                <Select
                                    value={alternativeId}
                                    label={t("Question.Edit.Feedback.SelectAlternativeLabel")}
                                    onChange={(e) => setAlternativeId(e.target.value)}
                                    disabled={readonly || alternativesWrong.length == 0}
                                >
                                {
                                        alternativesWrong?.map((x) => (
                                            <MenuItem key={x.alternativeId} value={x.alternativeId}>
                                                {x.text}
                                            </MenuItem>
                                    ))
                                }
                                </Select>
                            </FormControl>
                        </Box>
                        <MprcFormSubHeader
                            title={t("Question.Edit.Feedback.FeedbackLabel")}
                        />
                        <TextField
                            fullWidth
                            name="feedbackalternative"
                            autoComplete="off"
                            margin="dense"
                            value={viewData.alternatives.find(x => x.alternativeId == alternativeId)?.evaluation?.feedback || ""}
                            onChange={(e) => handleUpdateAlternativeFeedback(e.target.value)}
                            multiline
                            disabled={readonly}
                            maxRows={3}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 512 }
                                }
                            }}
                        />
                    </MprcContainer>
                    :
                    <MprcContainer>
                        <MprcFormSubHeader
                            title={t("Question.Edit.Feedback.FeedbackLabel")}
                        />
                        <TextField
                            fullWidth
                            name="feedback"
                            autoComplete="off"
                            margin="dense"
                            value={viewData.evaluation?.feedback || ""}
                            onChange={(e) => handleUpdateEvaluation("feedback", e.target.value)}
                            multiline
                            disabled={readonly}
                            maxRows={3}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 512 }
                                }
                            }}
                        />
                    </MprcContainer>
            }
            <MprcContainer>
                <MprcFormSubHeader
                    title={t("Question.Edit.Feedback.RecommendationTitle")}
                />
                <Stack direction="column" spacing={1} marginTop={1}>
                    <Stack direction="row" spacing={2}>
                        <Chip
                            size="small"
                            label={`+ ${t("Question.Edit.Feedback.PublicationLabel")}`}
                            disabled={(readonly || totalRecomendation >= 5 || alternativesWrong.length == 0) }
                            sx={(theme) => ({
                                mr: 1,
                                backgroundColor: theme.palette.highlight.main,
                                color: theme.palette.common.white,
                                textTransform: "uppercase"
                            })}
                            onClick={() => {
                                setDialog(
                                    <PublicationSelectorDrawer
                                        onlyPostPublications
                                        onClose={(data) => {
                                            setDialog(null);
                                            if (data != null && data.length > 0) {
                                                var newItem = { key: data[0].publicationId, label: data[0].title } as AutoCompleteDataModelDto;
                                                if (!viewData.publicationRecommended.some(x => x.key == newItem.key)) {
                                                    handleUpdatePublication([...publicationRecommended, newItem]);
                                                } else {
                                                    enqueueSnackbar(t("Question.Edit.Feedback.PublicationAlreadySelected"), { variant: "error" });
                                                }
                                            }
                                        }}
                                    />
                                );
                            }}
                        />
                        <Chip
                            size="small"
                            label={`+ ${t("Question.Edit.Feedback.TrainingLabel")}`}
                            disabled={readonly || totalRecomendation >= 5 || alternativesWrong.length == 0}
                            sx={(theme) => ({
                                mr: 1,
                                backgroundColor: theme.palette.highlight.main,
                                color: theme.palette.common.white,
                                textTransform: "uppercase"
                            })}
                            onClick={() => {
                                setDialog(
                                    <TrainingSelectorDrawer
                                        onlyManage={false}
                                        onClose={(data) => {
                                            setDialog(null);
                                            if (data != null && data.length > 0) {
                                                var newItem = { key: data[0].trainingId, label: data[0].name } as AutoCompleteDataModelDto;
                                                if (!viewData.trainingRecommended.some(x => x.key == newItem.key)) {
                                                    handleUpdateTraining([...trainingRecommended, newItem]);
                                                } else {
                                                    enqueueSnackbar(t("Question.Edit.Feedback.TrainingAlreadySelected"), { variant: "error" });
                                                }
                                            }
                                        }}
                                    />
                                );
                            }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                        {
                            publicationRecommended.map((item) => {
                                return (
                                    <Chip
                                        key={item.key}
                                        size="small"
                                        label={item.label}
                                        onDelete={() => handleUpdatePublication(publicationRecommended.filter(x => x.key != item.key))}
                                    />
                                )
                            })
                        }
                        {
                            trainingRecommended.map((item) => {
                                return (
                                    <Chip
                                        key={item.key}
                                        size="small"
                                        label={item.label}
                                        onDelete={() => handleUpdateTraining(viewData.trainingRecommended.filter(x => x.key != item.key))}
                                    />
                                )
                            })
                        }
                    </Stack>
                    {
                        !!currentPostValidation.knowledgePublicationRecommended && currentPostValidation?.knowledgePublicationRecommended &&
                            <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.knowledgePublicationRecommended} />}</FormHelperText>
                    }
                    {
                        !!currentPostValidation.trainingRecommended && currentPostValidation?.trainingRecommended &&
                            <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.trainingRecommended} />}</FormHelperText>
                    }
                    <FormHelperText>
                        {t("Question.Edit.Feedback.Recomendation_L1")} <br />
                        {t("Question.Edit.Feedback.Recomendation_L2")} <br />
                        {t("Question.Edit.Feedback.Recomendation_L3")}
                    </FormHelperText>
                </Stack>
            </MprcContainer>
        </>
    );
};
